<template>
  <v-container fluid v-if="!userIsLoggedIn" class="d-flex justify-center overflow-auto">
    <!-- login form -->
    <LoginForm ref="loginForm" :stepToShow="loginStep" @onLoginSubmit="handleLogin" :loading="loading.login" :errors="loginErrors" />
  </v-container>
  <Layout v-else-if="canShowForm && userIsLoggedIn" :showNav="step == 1" :showSort="showSort" :alignTop="step == 1">
    <!-- left column -->
    <template #content-left>
      <!-- navigation: course booking, halls booking, my bookings -->
      <v-list dense two-line class="pa-0" v-if="step == 1">
        <v-expansion-panels v-model="navigationExpansion">
          <v-expansion-panel>
            <v-expansion-panel-header>Navigation</v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-list-item-group :value="selectedNavigationItem" mandatory color="primary" :class="currentBreakpoint <= 3 ? 'd-flex' : ''">
                <!-- courses -->
                <NavigationItem
                  @onClick="handleNavigationItemClick"
                  :disabled="courseNavigationDisabled || isLoading"
                  :icon="'mdi-weight-lifter'"
                  :label="$t('overview.tab_label_courses') + ' ' + (courseNavigationDisabled ? $t('overview.tab_label_deactivated') : '')"
                  :subLabel="navSubTitleCourses"
                  :value="NAV_COURSEBOOKING" />

                <!-- halls -->
                <NavigationItem
                  @onClick="handleNavigationItemClick"
                  :disabled="hallNavigationDisabled || isLoading"
                  :icon="'mdi-soccer-field'"
                  :label="$t('overview.tab_label_halls') + ' ' + (hallNavigationDisabled ? $t('overview.tab_label_deactivated') : '')"
                  :subLabel="navSubTitleHalls"
                  :value="NAV_HALLBOOKING" />

                <!-- my bookings -->
                <NavigationItem
                  @onClick="handleNavigationItemClick"
                  :disabled="isLoading"
                  :icon="'mdi-account-cash'"
                  :label="$t('overview.tab_label_myBookings')"
                  :subLabel="$t('overview.tab_subLabel_myBookings')"
                  :value="NAV_MYBOOKINGS" />
              </v-list-item-group>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <AccountInfoVue :loading="isLoading" @onLogoutClick="handleLogout" />
      </v-list>
    </template>

    <!-- center column -->
    <template #content-center>
      <!-- logout confirmation dialog for mobile users -->
      <v-dialog v-model="showLogoutDialog" persistent max-width="290">
        <v-card>
          <v-card-title class="text-h5"> {{ $t('common.label_logout') }} </v-card-title>
          <v-card-text> {{ $t('common.logout_confirmation_message') }} </v-card-text>
          <v-card-actions>
            <v-btn color="green darken-1" text @click="resetApplication()"> {{ $t('common.label_yes') }} </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="red darken-1" text @click="showLogoutDialog = false"> {{ $t('common.label_no') }} </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- overview -->
      <v-tabs-items v-if="step == 1" v-model="selectedNavigationItem" style="width: 100%" touchless>
        <!-- courses -->
        <v-tab-item :value="NAV_COURSEBOOKING" disabled>
          <Overview
            v-if="step == 1 && selectedNavigationItem === NAV_COURSEBOOKING"
            @stepChanged="handleOverviewStepChange"
            @onPreviewItemSelect="handleCourseSelection"
            @onRemoveBookingButtonClick="handleCourseBookingCancellation"
            @onRemoveReservationButtonClick="handleCourseReservationCancellation"
            @onAddReservationButtonClick="handleCourseReservation"
            @onFinishedPayment="handleFinishedPayment"
            :isBooking="loading.booking"
            :isReservating="loading.reservating"
            :searchFor="currentSearch"
            :sortBy="currentSort"
            :filterFor="currentFilter"
            :bookingType="TYPE_COURSES"
            :loadingElem="loading.details" />
        </v-tab-item>

        <!-- halls -->
        <v-tab-item :value="NAV_HALLBOOKING">
          <Overview
            v-if="step == 1 && selectedNavigationItem === NAV_HALLBOOKING"
            @stepChanged="handleOverviewStepChange"
            @onPreviewItemSelect="handleHallSelection"
            @onRemoveBookingButtonClick="handleHallBookingCancellation"
            @onHallBookingInput="handleHallBookingInput"
            @onMeetingClick="handleMeetingClick"
            @onFinishedPayment="handleFinishedPayment"
            :searchFor="currentSearch"
            :sortBy="currentSort"
            :filterFor="currentFilter"
            :bookingType="TYPE_HALLS"
            :loadingElem="loading.details" />
        </v-tab-item>

        <!-- my bookings -->
        <v-tab-item :value="NAV_MYBOOKINGS">
          <Overview
            v-if="step == 1 && selectedNavigationItem === NAV_MYBOOKINGS"
            @stepChanged="handleOverviewStepChange"
            @onPreviewItemSelect="handleMyBookingsSelection"
            @onRemoveBookingButtonClick="handleBookingCancellationMyBookings"
            @onRemoveReservationButtonClick="handleCourseReservationCancellation"
            @onHallBookingInput="handleHallBookingInput"
            @onMeetingClick="handleMeetingClick"
            @onFinishedPayment="handleFinishedPayment"
            :isBooking="loading.booking"
            :isReservating="loading.reservating"
            :loadingElem="loading.details"
            :bookingType="TYPE_BOOKINGS" />
        </v-tab-item>
      </v-tabs-items>
    </template>

    <!-- right column -->
    <template #content-right>
      <!-- course search, sorts and filters -->
      <OverviewSorter
        v-if="selectedNavigationItem == NAV_COURSEBOOKING"
        @onSearch="handleSearch"
        @onSort="handleSort"
        @onFilter="handleFilter"
        :disabled="isLoading"
        :availableSorts="getCourseSorts()"
        :availableFilters="getCourseFilters()"
        :bookingType="TYPE_COURSES" />

      <!-- hall search, sorts and filters -->
      <OverviewSorter
        v-if="selectedNavigationItem == NAV_HALLBOOKING"
        @onSearch="handleSearch"
        @onSort="handleSort"
        @onFilter="handleFilter"
        :disabled="isLoading"
        :availableSorts="getHallSorts()"
        :availableFilters="getHallFilters()"
        :bookingType="TYPE_HALLS" />
    </template>
  </Layout>
</template>

<script>
// vuex
import { Mutators, GET_SESSIONDATA } from '@/store/storeMutator.js';
import { mapGetters } from 'vuex';

// mixins
import BookingMixin from '@/mixins/booking.js';
import ConstantsMixin from '@/mixins/constants.js';
import Sorts from '@/mixins/sorts.js';
import Responsive from '@/mixins/responsive.js';

// util
import { Semaphore } from '@/assets/Semaphore.js';
import { Api } from '@/api/apiAccess.ts';

// components
import NavigationItem from '@/components/controls/NavigationItem.vue';
import Overview from '@/components/booking/Overview.vue';
import OverviewSorter from '@/components/booking/OverviewSorter.vue';
import LoginForm from '@/components/login/LoginForm.vue';
import Layout from '@/components/Layout.vue';
import AccountInfoVue from '@/components/booking/AccountInfo.vue';

export default {
  name: 'Index',

  props: {
    clubId: String,
  },

  components: {
    NavigationItem,
    Overview,
    OverviewSorter,
    LoginForm,
    Layout,
    AccountInfoVue,
  },

  mixins: [BookingMixin, ConstantsMixin, Sorts, Responsive],

  created: function () {
    // set default selected tab to login
    this.selectedNavigationItem = this.NAV_LOGIN_ID;
  },

  mounted: async function () {
    this.selectedNavigationItem = this.NAV_LOGIN_ID;
    this.canShowForm = true;
    // on mobile devices we want the navigation to be small
    this.navigationExpansion = this.isMobile() ? undefined : 0;

    if (localStorage.getItem('logoutFlag') === 'true') {
      await this.$nextTick();
      this.$root.snackbar.show(this.$root.snackbars.logoutSuccess);
      // delete the flag from the localstorage
      localStorage.removeItem('logoutFlag');
    }
  },

  data: () => {
    return {
      /**
       * 0  -  login form, registration form, club registration form
       *
       * 1  -  course booking, hall booking, my bookings
       */
      step: 0,
      registrationStep: 0,
      loginErrors: [],
      loginStep: 0,
      overviewStep: 0,
      navigationExpansion: undefined, // will be 1 if not on mobile; undefined when on mobile
      canShowForm: false, // will be true after loading default data
      semaphores: {
        details: new Semaphore(1),
        booking: new Semaphore(1),
        meeting: new Semaphore(1),
      },
      loading: {
        reservating: false,
        register: false,
        booking: false,
        login: false,
        details: '', // will contain the id of the loading course / hall
      },
      currentSearch: '',
      currentSort: {},
      currentFilter: '',
      selectedNavigationItem: '',
      showLogoutDialog: false,
      requiredDataLoaded: false,
    };
  },

  methods: {
    //#region eventhandlers
    /**
     * displays a confirmation dialogue on mobile devices. On devices with mouse guidance, there is a tooltip that explains what the FAB does.
     */
    handleLogout() {
      if (this.isMobile()) {
        this.showLogoutDialog = true;
      } else {
        this.resetApplication();
      }
    },

    /**
     * handles the login and prepares the page to display the overview
     */
    async handleLogin(loginData) {
      this.loading.login = true;

      // remove errors from ui
      this.loginErrors.length = 0;

      var PersonenLoginViewModel = {
        VereinsNummer: this.clubId,
        AddressNummer: loginData.login,
        Passwort: loginData.password,
      };

      var loginResponse = await this.loginUser(PersonenLoginViewModel, this.clubId);

      // handle errors/response codes
      // password or addressnumber was null
      if (loginResponse == -1) this.loginErrors.push(this.$t('errors.loginPwAdrNrEmpty'));

      // club db could not be determined
      if (loginResponse == -2) this.loginErrors.push(this.$t('errors.loginClubDbNotFound'));

      // addressnumber was not found in the club
      if (loginResponse == -3) this.loginErrors.push(this.$t('errors.logiAdrNrNotFound'));

      // wrong password
      if (loginResponse == -4) this.loginErrors.push(this.$t('errors.loginWrongPassword'));

      // unknown error
      if (loginResponse == -5) this.loginErrors.push(this.$t('errors.genericUnknownError'));

      // permissions could not be loaded
      if (loginResponse == -6) this.loginErrors.push(this.$t('errors.loginPermsCouldNotBeLoaded'));

      // clubmember has insufficient permissions
      if (loginResponse == -7) this.loginErrors.push(this.$t('errors.loginInsufficientPermissions'));

      //club data could not be loaded
      if (loginResponse == -8) this.loginErrors.push(this.$t('errors.loginClubDataCouldNotBeLoaded'));

      // success
      if (loginResponse === 0) {
        // show the user that the login was successful
        this.$root.snackbar.show(this.$root.snackbars.loginSuccess);
        this.step++;

        // determine which navigation may be selected
        const courseArr = this.SessionData.club.courses;
        const courseArrUndefined = courseArr === undefined || courseArr === null;
        const courseArrEmpty = courseArrUndefined ? true : courseArr.length === 0;
        const courseReadPerm = this.SessionData.member.permissions.KurseLesen;

        const hallArr = this.SessionData.club.halls;
        const hallArrUndefined = hallArr === undefined || hallArr === null;
        const hallArrEmpty = hallArrUndefined ? true : hallArr.length === 0;
        const hallReadPerm = this.SessionData.member.permissions.KurseLesen;

        var navigationItemToSelect = this.NAV_COURSEBOOKING;

        if (courseArrUndefined || courseArrEmpty || !courseReadPerm) {
          navigationItemToSelect = this.NAV_HALLBOOKING;
        }

        if (hallArrUndefined || hallArrEmpty || !hallReadPerm) {
          navigationItemToSelect = this.NAV_MYBOOKINGS;
        }

        this.selectedNavigationItem = navigationItemToSelect;
        this.requiredDataLoaded = true;
      }

      this.loading.login = false;
    },

    /** handles the click on a navigation item */
    handleNavigationItemClick(clickedNavigationItem) {
      if (clickedNavigationItem === this.selectedNavigationItem) return;

      // change navigation
      this.selectedNavigationItem = clickedNavigationItem;
      // update values
      this.overviewStep = 0;
      // on mobile, close the navigation expansion
      if (this.isMobile()) {
        this.navigationExpansion = false;
      }
    },

    /** handles the change of the step in the Overview */
    handleOverviewStepChange(newStep) {
      this.overviewStep = newStep;
    },
    //#endregion

    async handleMyBookingsSelection(selection) {
      if (this.getType(selection) === this.TYPE_HALLS) {
        await this.handleHallSelection(selection);
      } else if (this.getType(selection) === this.TYPE_COURSES) {
        await this.handleCourseSelection(selection);
      }
    },

    // #region course eventhandlers
    async handleCourseSelection(selectedCourse) {
      this.loading.details = selectedCourse.CourseId;

      await this.loadAdditionalCourseInfo(selectedCourse);

      /*
      since "Overview" watches for changes in the loading.details property
      to display the loading animation and have the details fade in nicely,
      the code here has to wait for the next time vue flushes the updates to the DOM

      flow:
      1. click on PreviewItem
      2. event "onSelect" is triggered
      3. this method is called and sets loading.details to the ID of the PreviewItem
      4. method runs and loads data that has not been loaded yet
      5. loading.details is set to ""
         --> before step 5 the code must wait for the next tick / DOM update flush,
             otherwise the watcher for loadingCourse in "Overview" doesn't notice the change
      */
      await this.$forceUpdate();
      await this.$nextTick();

      this.loading.details = '';
    },

    async handleHallSelection(selectedHall) {
      this.loading.details = selectedHall.Id;

      await this.loadHallDetails(selectedHall.Id);

      await this.$nextTick();

      this.loading.details = '';
    },

    /** handles the course booking cancellation */
    async handleCourseBookingCancellation(courseToBeCancelled) {
      if (!courseToBeCancelled.booked) return;
      this.loading.booking = true;

      // cancel booking
      var bookingCancellationData = (await Api.HaKuBu.haKuBuRemoveCourseBooking(this.clubId, courseToBeCancelled.CourseId)).data;

      // error(code) handling
      if (bookingCancellationData === null || bookingCancellationData === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }

      // course booking was not found or user havent booked this course
      if (bookingCancellationData === -1) this.$root.snackbar.show(this.$root.snackbars.courseBookingRemoveCourseOrMemberNotFound);

      // course booking cannot be cancelled (anymore)
      if (bookingCancellationData === -2) this.$root.snackbar.show(this.$root.snackbars.courseBookingRemoveCourseCannotBeCancelled);

      if (bookingCancellationData === 0) {
        // update course
        await this.updateCourse(courseToBeCancelled.CourseId);
        this.$root.snackbar.show(this.$root.snackbars.courseBookingRemoveSuccess);
      }

      this.loading.booking = false;
    },

    /** handles the course reservation */
    async handleCourseReservation(courseToBeReservated) {
      if (!courseToBeReservated) return;
      if (courseToBeReservated.reserved) return;

      this.loading.reservating = true;

      // save reservation
      var reservationData = (await Api.HaKuBu.haKuBuPostSaveCourseReservation(this.clubId, courseToBeReservated.CourseId)).data;

      // error(code) handling
      if (reservationData === null || reservationData === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }

      // course was not found or course is not reservable
      if (reservationData === -1) this.$root.snackbar.show(this.$root.snackbars.reservationAddNoCourseFoundOrNotReservable);

      // course is full and cannot be reserved (depends on configuration)
      if (reservationData === -2) this.$root.snackbar.show(this.$root.snackbars.reservationAddNoReservationForFullCourses);

      // course is already reserved
      if (reservationData === -3) this.$root.snackbar.show(this.$root.snackbars.reservationAddAlreadyReserved);

      if (reservationData === 0) {
        // update course
        await this.updateCourse(courseToBeReservated.CourseId);
        this.$root.snackbar.show(this.$root.snackbars.reservationAddSuccess);
      }

      this.loading.reservating = false;
    },

    /** handles the course reservation cancellation */
    async handleCourseReservationCancellation(courseToBeCancelled) {
      if (!courseToBeCancelled) return;

      if (!courseToBeCancelled.reserved) return;

      this.loading.reservating = true;

      // remove reservation
      var reservationCancellationData = (await Api.HaKuBu.haKuBuRemoveCourseReservation(this.clubId, courseToBeCancelled.CourseId)).data;

      // error(code) handling
      if (reservationCancellationData === null || reservationCancellationData === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        return;
      }
      // member was not found in course reservations
      if (reservationCancellationData === -1) this.$root.snackbar.show(this.$root.snackbars.reservationRemoveNoCourseOrMemberNotFound);

      // course isnt reseved
      if (reservationCancellationData === -2) this.$root.snackbar.show(this.$root.snackbars.reservationRemoveNoReservation);

      if (reservationCancellationData === 0) {
        // update course
        await this.updateCourse(courseToBeCancelled.CourseId);
        this.$root.snackbar.show(this.$root.snackbars.reservationRemoveSuccess);
      }

      this.loading.reservating = false;
    },
    // #endregion

    //#region hall eventhandlers
    /**
     * processes the data from the booking dialog
     * loads the corresponding meetings
     */
    async handleHallBookingInput(hallToBeBooked, bookingData) {
      this.semaphores.booking.lock();

      // get free meetings
      var freeMeetings = (
        await Api.HaKuBu.haKuBuGetFreeDates(
          this.clubId,
          hallToBeBooked.Id,
          bookingData.date,
          bookingData.timeFrom,
          bookingData.timeTo,
          bookingData.duration,
          bookingData.wholeDay,
        )
      ).data;

      let responseCode = freeMeetings.Result;

      // error(code) handling
      if (freeMeetings === undefined || freeMeetings === null) this.$root.snackbar.show(this.$root.snackbars.genericError);

      // from, to or duration is null
      if (responseCode === -1) this.$root.snackbar.show(this.$root.snackbars.hallBookingMeetingFromToDurationNull);

      // hall could not be loaded
      if (responseCode === -2) this.$root.snackbar.show(this.$root.snackbars.hallBookingMeetingHallCouldNotBeFound);

      // bookingblock smaller min booking duration
      if (responseCode === -3)
        this.$root.snackbar.show(this.$root.snackbars.hallBookingMeetingDurationSmallerMinBookingDuration(freeMeetings.MinimalBookingDuration));

      if (responseCode === 0) {
        // update hall in vuex store
        hallToBeBooked.meetings = freeMeetings.DateRanges;
        Mutators.updateHall(hallToBeBooked);
      }

      this.semaphores.booking.release();
    },

    /** calculates the rent for the currently selected meeting */
    async handleMeetingClick(hall, bookingData) {
      this.semaphores.meeting.lock();
      this.loading.meeting = hall.Id;

      var rent = (
        await Api.HaKuBu.haKuBuCalculateHallRent(
          this.clubId,
          hall.Id,
          bookingData.date,
          bookingData.timeFrom,
          bookingData.timeTo,
          bookingData.wholeDay,
        )
      ).data;

      if (rent === null || rent === undefined) {
        this.$root.snackbar.show(this.$root.snackbars.genericError);
        this.semaphores.meeting.release();
        this.loading.meeting = '';
        return;
      }

      var meeting = bookingData.selectedMeeting;
      meeting.rent = rent;

      Mutators.updateHallMeeting(hall, meeting);

      this.semaphores.meeting.release();
      this.loading.meeting = '';
    },

    /** handles the hall booking cancellation (meetingId = "" --> cancels all meetings!) */
    async handleHallBookingCancellation(hallToBeCancelled, meetingId = '') {
      this.semaphores.booking.lock();
      this.loading.booking = true;

      if (meetingId == '')
        meetingId = hallToBeCancelled.BookedDates.filter((bookedMeeting) => !bookedMeeting.IsPayed).map((bookedMeeting) => bookedMeeting.Id);

      await Api.HaKuBu.haKuBuDeleteHallBookings(this.clubId, meetingId);

      // update hall
      await this.updateHall(hallToBeCancelled.Id);

      if (Array.isArray(meetingId) && meetingId.length > 1) {
        this.$root.snackbar.show(this.$root.snackbars.hallBookingRemoveAllSuccess);
      } else {
        this.$root.snackbar.show(this.$root.snackbars.hallBookingRemoveSuccess);
      }

      this.loading.booking = false;
      this.semaphores.booking.release();
    },
    //#endregion

    /** handles the booking cancellation when in the "Meine Buchungen" area */
    handleBookingCancellationMyBookings(itemToBeCancelled, meetingIds = ['']) {
      var itemType = this.getType(itemToBeCancelled);

      if (itemType == this.TYPE_COURSES) {
        this.handleCourseBookingCancellation(itemToBeCancelled);
      } else if (itemType == this.TYPE_HALLS) {
        this.handleHallBookingCancellation(itemToBeCancelled, meetingIds);
      }
    },

    handleFinishedPayment(navigateToMyBookings) {
      this.loading.booking = false;

      if (navigateToMyBookings) this.handleNavigationItemClick(this.NAV_MYBOOKINGS);
    },

    /** synchronizes the input values from the login form with the registration form and vice versa */
    syncForms(clickedNavigationItem) {
      if (clickedNavigationItem === this.NAV_LOGIN_ID) {
        this.$refs.loginForm.loginData = this.$refs.registrationForm.loginData;
      } else if (clickedNavigationItem === this.NAV_REGISTRATION_ID) {
        this.$refs.registrationForm.loginData = this.$refs.loginForm.loginData;
      }
    },

    // #region search/sort/filter
    handleSearch(searchValue) {
      this.currentSearch = searchValue;
    },

    handleSort(sortObj) {
      this.currentSort = sortObj;
    },

    handleFilter(filterObjKey) {
      this.currentFilter = filterObjKey;
    },
    // #endregion

    // #region Look & Feel
    generateNavSubTitleAddon(icon, addon) {
      let addonText = '';
      addonText += `<div style="flex: 1 1 100%; vertical-align: middle;">
                 <span style="font-size:20px" class="${icon}"></span> 
                 ${addon}
               </div>`;
      return addonText;
    },
    //#endregion
    resetApplication() {
      // set a flag in the localstorage to be able to display the "successfully logged" out message later on
      localStorage.setItem('logoutFlag', 'true');
      this.$router.go();
    },
  },

  computed: {
    ...mapGetters({ SessionData: GET_SESSIONDATA }),

    userIsLoggedIn() {
      var authToken = this.SessionData.member?.authToken ? true : false;
      var addressNumber = this.SessionData.member?.addressNumber ? true : false;

      return authToken && addressNumber && this.requiredDataLoaded;
    },

    isLoading() {
      return this.loading.reservating || !this.isNoWs(this.loading.details);
    },

    showSort() {
      return this.selectedNavigationItem != this.NAV_MYBOOKINGS && this.overviewStep == 0 && this.step > 0;
    },

    //#region Look & Feel
    courseNavigationDisabled() {
      const courseArr = this.SessionData.club.courses;
      const courseArrUndefined = courseArr === undefined || courseArr === null;
      const courseArrEmpty = courseArrUndefined ? true : courseArr.length === 0;
      const courseReadPerm = this.SessionData.member.permissions.KurseLesen;

      return courseArrEmpty || !courseReadPerm;
    },

    hallNavigationDisabled() {
      const hallArr = this.SessionData.club.halls;
      const hallArrUndefined = hallArr === undefined || hallArr === null;
      const hallArrEmpty = hallArrUndefined ? true : hallArr.length === 0;
      const hallsReadPerm = this.SessionData.member.permissions.HallenLesen;

      return hallArrUndefined || hallArrEmpty || !hallsReadPerm;
    },

    navSubTitleCourses() {
      const courseArr = this.SessionData.club.courses;
      const courseArrUndefined = courseArr === undefined || courseArr === null;
      const courseArrEmpty = courseArrUndefined ? true : courseArr.length === 0;
      const courseReadPerm = this.SessionData.member.permissions.KurseLesen;

      const defaultSubTitle = this.$t('overview.tab_subLabel_courses');
      const noCoursesAddition = this.$t('overview.tab_label_nocoursesavail');
      const noReadPermAddition = this.$t('overview.tab_label_noreadperm');

      var subTitle = defaultSubTitle;

      if (courseArrUndefined || courseArrEmpty || !courseReadPerm) {
        subTitle = '<div class="d-flex flex-column justify-start red--text text--lighten-2" style="gap: 4px;">';

        if (courseArrUndefined || courseArrEmpty) {
          subTitle = subTitle + this.generateNavSubTitleAddon('mdi mdi-playlist-remove', noCoursesAddition);
        }

        if (!courseReadPerm) {
          subTitle = subTitle + this.generateNavSubTitleAddon('mdi mdi-account-remove', noReadPermAddition);
        }

        subTitle = subTitle + '</div>';
      }

      return subTitle;
    },
    navSubTitleHalls() {
      const hallArr = this.SessionData.club.halls;
      const hallArrUndefined = hallArr === undefined || hallArr === null;
      const hallArrEmpty = hallArrUndefined ? true : hallArr.length === 0;
      const hallsReadPerm = this.SessionData.member.permissions.HallenLesen;

      const defaultSubTitle = this.$t('overview.tab_subLabel_halls');
      const noHallsAddition = this.$t('overview.tab_label_nohallsavail');
      const noReadPermAddition = this.$t('overview.tab_label_noreadperm');

      var subTitle = defaultSubTitle;

      if (hallArrUndefined || hallArrEmpty || !hallsReadPerm) {
        subTitle = '<div class="d-flex flex-column justify-start red--text text--lighten-2" style="gap: 4px;">';

        if (hallArrUndefined || hallArrEmpty) subTitle = subTitle + this.generateNavSubTitleAddon('mdi mdi-playlist-remove', noHallsAddition);

        if (!hallsReadPerm) subTitle = subTitle + this.generateNavSubTitleAddon('mdi mdi-account-remove', noReadPermAddition);

        subTitle = subTitle + '</div>';
      }

      return subTitle;
    },
    //#endregion
  },

  watch: {
    /** resets all data when changing to another booking overview */
    step(newStep) {
      if (newStep !== 1) return;

      this.loading.reservating = false;
      this.loading.register = false;
      this.loading.booking = false;
      this.loading.login = false;
      this.loading.details = '';

      this.currentSearch = '';
      this.currentSort = {};
      this.currentFilter = '';

      this.semaphores.details.release();
      this.semaphores.booking.release();
      this.semaphores.meeting.release();
    },
  },
};
</script>

<style scoped>
.v-window.v-item-group {
  overflow: auto;
}
</style>
